import { memo, type ReactNode } from "react";
import type { ParsedTerminalContextEntry } from "~/lib/terminalContext";
import { UserMessageText } from "./UserMessageText";
import { TerminalContextInlineChip } from "./TerminalContextInlineChip";
import {
  buildInlineTerminalContextText,
  formatTerminalContextHeaderLabel,
  textContainsInlineTerminalContextLabels,
} from "./userMessageTerminalContexts";

const UserMessageTerminalContextInlineLabel = memo(
  function UserMessageTerminalContextInlineLabel(props: {
    readonly context: ParsedTerminalContextEntry;
  }) {
    const tooltipText =
      props.context.body.length > 0
        ? `${props.context.header}\n${props.context.body}`
        : props.context.header;

    return <TerminalContextInlineChip label={props.context.header} tooltipText={tooltipText} />;
  },
);

function embeddedTerminalContextNodes(
  text: string,
  terminalContexts: readonly ParsedTerminalContextEntry[],
): ReactNode[] | null {
  const inlineNodes: ReactNode[] = [];
  let cursor = 0;

  for (const context of terminalContexts) {
    const label = formatTerminalContextHeaderLabel(context.header);
    const matchIndex = text.indexOf(label, cursor);
    if (matchIndex === -1) {
      return null;
    }
    if (matchIndex > cursor) {
      inlineNodes.push(
        <span key={`user-terminal-context-inline-before:${context.header}:${cursor}`}>
          <UserMessageText text={text.slice(cursor, matchIndex)} />
        </span>,
      );
    }
    inlineNodes.push(
      <UserMessageTerminalContextInlineLabel
        key={`user-terminal-context-inline:${context.header}`}
        context={context}
      />,
    );
    cursor = matchIndex + label.length;
  }

  if (inlineNodes.length === 0) return null;
  if (cursor < text.length) {
    inlineNodes.push(
      <span key={`user-message-terminal-context-inline-rest:${cursor}`}>
        <UserMessageText text={text.slice(cursor)} />
      </span>,
    );
  }
  return inlineNodes;
}

export const UserMessageBody = memo(function UserMessageBody(props: {
  readonly text: string;
  readonly terminalContexts: readonly ParsedTerminalContextEntry[];
}) {
  if (props.terminalContexts.length > 0) {
    const hasEmbeddedInlineLabels = textContainsInlineTerminalContextLabels(
      props.text,
      props.terminalContexts,
    );
    const inlinePrefix = buildInlineTerminalContextText(props.terminalContexts);
    const inlineNodes: ReactNode[] = [];

    const embeddedNodes = hasEmbeddedInlineLabels
      ? embeddedTerminalContextNodes(props.text, props.terminalContexts)
      : null;
    if (embeddedNodes) {
      return (
        <div className="whitespace-pre-wrap wrap-break-word text-sm leading-relaxed text-foreground">
          {embeddedNodes}
        </div>
      );
    }

    for (const context of props.terminalContexts) {
      inlineNodes.push(
        <UserMessageTerminalContextInlineLabel
          key={`user-terminal-context-inline:${context.header}`}
          context={context}
        />,
      );
      inlineNodes.push(
        <span key={`user-terminal-context-inline-space:${context.header}`} aria-hidden="true">
          {" "}
        </span>,
      );
    }

    if (props.text.length > 0) {
      inlineNodes.push(
        <span key="user-message-terminal-context-inline-text">
          <UserMessageText text={props.text} />
        </span>,
      );
    } else if (inlinePrefix.length === 0) {
      return null;
    }

    return (
      <div className="whitespace-pre-wrap wrap-break-word text-sm leading-relaxed text-foreground">
        {inlineNodes}
      </div>
    );
  }

  if (props.text.length === 0) {
    return null;
  }

  return (
    <div className="whitespace-pre-wrap wrap-break-word text-sm leading-relaxed text-foreground">
      <UserMessageText text={props.text} />
    </div>
  );
});
